import { useState } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { route } from '../../utils/route';

interface PaymentMethod {
  id: number;
  name: string;
  icon: 'cash' | 'mercadopago' | 'card' | 'modo' | 'bank_transfer' | 'qr';
  disabled: boolean;
  disabledReason?: string;
  qr?: boolean;
}

interface Order {
  id: number;
  formatted_number: string;
  total: number;
}

interface DirectPaymentDiscount {
  percent: number;
  savings: number;
}

interface SelectPaymentMethodProps {
  order: Order;
  paymentMethods: PaymentMethod[];
  withErrorMessage: boolean;
  directPaymentDiscount: DirectPaymentDiscount | null;
}

const methodIcons: Record<PaymentMethod['icon'], React.ReactNode> = {
  cash: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-6 w-6">
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <circle cx="12" cy="12" r="3" />
      <path d="M6 12h.01M18 12h.01" />
    </svg>
  ),
  mercadopago: (
    <img src="/img/mercadopago-mono.svg" alt="MercadoPago" className="h-6 w-6" />
  ),
  card: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-6 w-6">
      <rect x="1" y="4" width="22" height="16" rx="2" />
      <path d="M1 10h22" />
    </svg>
  ),
  modo: (
    <img src="/img/modo.svg" alt="MODO" className="h-6 w-auto" />
  ),
  bank_transfer: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-6 w-6">
      <path d="M3 21h18M3 7v14M21 7v14M6 21V11M12 21V11M18 21V11M6 7h12M12 3L22 7H2L12 3z" />
    </svg>
  ),
  qr: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-6 w-6">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="3" height="3" />
      <path d="M21 14h-3v3M21 21h-3v-4M18 21h3" />
    </svg>
  ),
};

export default function SelectPaymentMethod({
  order,
  paymentMethods,
  withErrorMessage,
  directPaymentDiscount,
}: SelectPaymentMethodProps) {
  // Track selected index (not method id) since multiple entries can share the same id
  const firstAvailableIndex = paymentMethods.findIndex((m) => !m.disabled);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(
    firstAvailableIndex >= 0 ? firstAvailableIndex : null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContinue = () => {
    if (selectedIndex === null || isSubmitting) return;
    setIsSubmitting(true);
    const selected = paymentMethods[selectedIndex];
    if (selected.qr) {
      window.location.href = route('orders.pay_qr', { id: order.id });
    } else {
      window.location.href = route('orders.pay', { id: order.id, method: selected.id });
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <MainLayout>
      <Head>
        <title>Seleccionar método de pago | Abasto Virtual</title>
        <meta name="robots" content="noindex" />
      </Head>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 px-4 py-10 md:py-16">
        <div className="mx-auto max-w-md">
          {/* Card */}
          <div className="rounded-2xl bg-white p-6 shadow-xl shadow-gray-200/50 md:p-8">
            {/* Header */}
            <div className="mb-6 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-7 w-7 text-green-600"
                >
                  <rect x="1" y="4" width="22" height="16" rx="2" />
                  <path d="M1 10h22" />
                </svg>
              </div>
              <h1 className="text-xl font-bold text-gray-900 md:text-2xl">
                ¿Cómo querés pagar?
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Elegí el método de pago para tu pedido
              </p>
            </div>

            {/* Order summary */}
            <div className="mb-6 flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
              <span className="text-sm text-gray-500">
                Pedido {order.formatted_number}
              </span>
              <span className="text-lg font-bold text-gray-900">
                {formatPrice(order.total)}
              </span>
            </div>

            {/* Error message */}
            {withErrorMessage && (
              <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
                <svg
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4M12 16h.01" />
                </svg>
                <p className="text-sm text-red-700">
                  Hubo un problema al procesar tu pago. Por favor, intentá de nuevo o seleccioná otro método.
                </p>
              </div>
            )}

            {/* Payment methods */}
            <div className="mb-6 space-y-3">
              {paymentMethods.map((method, index) => (
                <button
                  key={index}
                  type="button"
                  disabled={method.disabled}
                  onClick={() => !method.disabled && setSelectedIndex(index)}
                  className={`
                    relative flex w-full items-center gap-4 rounded-xl border-2 p-4 text-left transition-all
                    ${method.disabled
                      ? 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-60'
                      : selectedIndex === index
                        ? 'border-green-500 bg-green-50 ring-2 ring-green-500/20'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                    }
                  `}
                >
                  {/* Radio indicator */}
                  <div
                    className={`
                      flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all
                      ${selectedIndex === index
                        ? 'border-green-500 bg-green-500'
                        : 'border-gray-300 bg-white'
                      }
                    `}
                  >
                    {selectedIndex === index && (
                      <svg
                        className="h-3 w-3 text-white"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="4"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>

                  {/* Icon */}
                  <div
                    className={`
                      flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg
                      ${selectedIndex === index ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}
                    `}
                  >
                    {methodIcons[method.icon]}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {method.name}
                      {method.icon === 'bank_transfer' && directPaymentDiscount && directPaymentDiscount.savings > 0 && (
                        <span className="ml-2 inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                          Recomendado
                        </span>
                      )}
                    </p>
                    {method.disabled && method.disabledReason && (
                      <p className="mt-0.5 text-xs text-gray-400">
                        {method.disabledReason}
                      </p>
                    )}
                    {method.qr && (
                      <p className="mt-0.5 text-xs text-gray-500">
                        Escaneá con Mercado Pago u otra billetera
                      </p>
                    )}
                  </div>

                  {/* Selected badge */}
                  {selectedIndex === index && (
                    <span className="rounded-full bg-green-500 px-2 py-0.5 text-xs font-medium text-white">
                      Seleccionado
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Direct payment discount banner */}
            {directPaymentDiscount && directPaymentDiscount.savings > 0 && (
              <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                💸 Pagando por transferencia ahorrás {formatPrice(directPaymentDiscount.savings)} en este pedido.
              </div>
            )}

            {/* Submit button */}
            <button
              type="button"
              onClick={handleContinue}
              disabled={selectedIndex === null || isSubmitting}
              className={`
                flex w-full items-center justify-center gap-2 rounded-xl px-6 py-4 text-base font-semibold transition-all
                ${selectedIndex === null || isSubmitting
                  ? 'cursor-not-allowed bg-gray-200 text-gray-400'
                  : 'bg-green-500 text-white hover:bg-green-600 active:scale-[0.98]'
                }
              `}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="h-5 w-5 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeDasharray="31.4 31.4"
                      strokeLinecap="round"
                    />
                  </svg>
                  Procesando...
                </>
              ) : (
                <>
                  Continuar
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </>
              )}
            </button>
          </div>

          {/* Back link */}
          <div className="mt-6 text-center">
            <Link
              href={route('orders.index')}
              className="inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-green-600"
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Volver a mis pedidos
            </Link>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
